import { useEffect, useRef, useState } from "react";
import LoadingScreen from "../../components/LoadingScreen";
import { MinusCircleIcon } from "@heroicons/react/24/solid";
import { setLike } from "../../stores/slices/openingWarehouseSlice";
import {
  addItemToLocalStorageArray,
  loadArrayFromLocalStorage,
  localStorageKeys,
  removeItemFromLocalStorageArray,
  StorageModel,
} from "../../utils/localStorage";
import { useDispatch } from "react-redux";
import {
  changeHeaderFilter,
  convertFilterIcon,
} from "../../utils/headerFilter";
import { getData, postData } from "../../apis";
import { API } from "../../apis/config";
import { ParcelProduct } from "../../interfaces/ParcelProduct";
import type { ParcelDiscountPolicy } from "../../interfaces/ParcelDiscount";
import { calculateParcelItems } from "../../utils/parcelDiscount";

// window.daum의 타입 정의
declare global {
  interface Window {
    daum: any;
  }
}

function CartProduct() {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ParcelProduct[]>([]);
  const dispatch = useDispatch();
  const inputRefs = useRef<Partial<Record<number, HTMLInputElement>>>({});
  const [sortedData, setSortedData] = useState<ParcelProduct[]>([]);
  const [headerFilter] = useState<{ [key: string]: number }>({});
  const [finalPrice, setFinalPrice] = useState(0);
  const [discountPolicy, setDiscountPolicy] =
    useState<ParcelDiscountPolicy | null>(null);
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  const [form, setForm] = useState({
    customerName: "",
    phoneNumber: "",
    address: "",
    detailAddress: "",
    totalPrice: 0,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const parcelProducts = calculateParcelItems(sortedData, discountPolicy);

    const payload = {
      customerName: form.customerName,
      phoneNumber: form.phoneNumber,
      address: form.address,
      detailAddress: form.detailAddress,
      totalPrice: finalPrice,
      parcelProducts,
    };

    try {
      await postData(API.PARCEL.SUBMIT, payload); // 택배 신청 API 호출
      alert("배송 신청이 완료되었습니다.");
      setForm((prev) => ({
        ...prev,
        customerName: "",
        phoneNumber: "",
        address: "",
        detailAddress: "",
      }));
    } catch (error) {
      console.error(error);
      alert("신청 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let discountedPrice = 0;
    let totalPrice = 0;

    calculateParcelItems(sortedData, discountPolicy).forEach((item) => {
      discountedPrice += item.finalPrice;
      totalPrice += item.unitPrice * item.quantity;
    });

    setFinalPrice(discountedPrice);
    setForm((prev) => ({ ...prev, totalPrice }));
  }, [sortedData, discountPolicy]);

  useEffect(() => {
    const data = async () => {
      const p = loadArrayFromLocalStorage(localStorageKeys.cartProduct);
      try {
        const [productsRes, discountRes] = await Promise.all([
          getData<ParcelProduct[]>(API.PARCEL.PRODUCTS),
          getData<ParcelDiscountPolicy>(API.PARCEL.DISCOUNT),
        ]);

        if (discountRes.isSuccess && discountRes.result) {
          setDiscountPolicy({
            thresholdAmount: Number(discountRes.result.thresholdAmount) || 0,
            discountRate: Number(discountRes.result.discountRate) || 0,
            excludedProducts: Array.isArray(
              discountRes.result.excludedProducts,
            )
              ? discountRes.result.excludedProducts
              : [],
          });
        } else {
          setDiscountPolicy({
            thresholdAmount: 0,
            discountRate: 0,
            excludedProducts: [],
          });
        }

        const productList = Array.isArray(productsRes.result)
          ? productsRes.result
          : [];
        let filtered_rst = productList.filter((model) =>
          p.some((rst) => rst.id === model.id),
        );

        // id가 일치하는 모델의 quantity 값을 수정
        filtered_rst.forEach((model) => {
          const matchingRst = p.find((rst) => rst.id === model.id);
          if (matchingRst) {
            model.quantity = matchingRst.quantity; // 원하는 값으로 수정
            model.isLike = matchingRst.isLike;
          }
        });

        setData(filtered_rst);
        setSortedData(filtered_rst);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    data();
  }, []);

  const openPost = () => {
    new window.daum.Postcode({
      oncomplete: (data: any) => {
        const roadAddr = data.roadAddress; // 도로명 주소
        const jibunAddr = data.jibunAddress; // 지번 주소
        setForm((prev) => ({
          ...prev,
          zipcode: data.zonecode,
          address: roadAddr || jibunAddr,
        }));
      },
    }).open();
  };

  const handleClick = (row: ParcelProduct) => {
    const newRow = { ...row, isLike: !row.isLike };
    if (row.isLike) {
      removeItemFromLocalStorageArray(localStorageKeys.cartProduct, newRow.id);
    } else {
      addItemToLocalStorageArray(localStorageKeys.cartProduct, {
        id: newRow.id,
        isLike: newRow.isLike,
        quantity: newRow.quantity,
      });
    }

    const p = data.filter(
      (item) => item.id !== newRow.id && item.isLike === true
    );
    setData(p);
    setSortedData(p);

    dispatch(setLike(newRow.id));
  };

  const clickHeader = (
    key: string,
    compareFn: (a: ParcelProduct, b: ParcelProduct) => number
  ) => {
    changeHeaderFilter(headerFilter, setSortedData, key, compareFn, data);
  };

  const createFilterIcon = (key: string): string => {
    return convertFilterIcon(headerFilter, key);
  };

  return (
    <div className="md:mx-20">
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div>
          <table className="mt-4 min-w-full bg-white text-xs md:text-base">
            <thead className="bg-gray-200  ">
              <tr>
                <th className="w-1/12 md:px-4 px-2 py-2 text-center text-gray-600">
                  수량
                </th>
                <th
                  className="md:w-1/3 w-1/2 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader("name", (a, b) =>
                      a.name.localeCompare(b.name, "ko")
                    );
                  }}
                >
                  상품명{createFilterIcon("name")}
                </th>
                <th
                  className="w-3/12 px-4 py-2 text-center text-gray-600 cursor-pointer"
                  onClick={() => {
                    clickHeader("location", (a, b) =>
                      a.location.localeCompare(b.location, "ko")
                    );
                  }}
                >
                  단가{createFilterIcon("location")}
                </th>
                <th className="w-1/12 md:px-4 px-0 py-2 text-center text-gray-600">
                  제외
                </th>
              </tr>
            </thead>
            <tbody className="text-base">
              {sortedData
                .slice()
                .reverse()
                .map((item) => (
                  <tr className="border-t" key={item.id}>
                    <td>
                      <input
                        type="number"
                        value={item.quantity}
                        className="w-full text-center text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        ref={(el) => {
                          inputRefs.current[item.id] = el ?? undefined;
                        }}
                        onClick={() => {
                          const el = inputRefs.current[item.id];
                          if (el) {
                            el.focus();
                            el.select();
                          }
                        }}
                        onChange={(e) => {
                          const localStorageData: StorageModel[] =
                            loadArrayFromLocalStorage(
                              localStorageKeys.cartProduct
                            );

                          // localstorage 에서 저장된 id로 검색
                          const target = data.find(
                            (e) =>
                              e.id ===
                              localStorageData.find(
                                (localStorageData) =>
                                  localStorageData.id === item.id
                              )?.id
                          );

                          const quantity = Number(e.target.value);
                          if (target && quantity > 0) {
                            target.quantity = quantity;
                            const p = data.map((item) => {
                              if (item.id === target.id) {
                                return target;
                              } else {
                                return item;
                              }
                            });
                            setData(p);
                            setSortedData(p);
                            removeItemFromLocalStorageArray(
                              localStorageKeys.cartProduct,
                              target.id
                            );
                            addItemToLocalStorageArray(
                              localStorageKeys.cartProduct,
                              {
                                id: target.id,
                                isLike: target.isLike,
                                quantity: target.quantity,
                              }
                            );
                          }
                        }}
                      />
                    </td>
                    <td className="px-4 py-2 text-sm md:text-base">
                      {item.name}
                    </td>
                    <td className="px-4 py-2 text-sm md:text-base">
                      {item.price.toLocaleString()}
                    </td>
                    <td className="px-4 py-2 ">
                      <button onClick={() => handleClick(item)}>
                        <MinusCircleIcon
                          className={`h-6 w-6 ${
                            item.isLike ? "text-red-500" : "text-black"
                          }`}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {/* 총합 & 할인 가격 표시 */}
          <div className="my-4 p-4 bg-gray-100 rounded-md text-right text-base md:text-lg">
            {(() => {
              const rateLabel = discountPolicy?.discountRate ?? 0;
              return (
                <>
                  <div>
                    총합:
                    <span className="font-bold">
                      {form.totalPrice.toLocaleString()}원
                    </span>
                  </div>
                  {finalPrice !==
                  sortedData.reduce(
                    (sum, item) => sum + item.price * item.quantity,
                    0
                  ) ? (
                    <div className="text-green-600">
                      할인 적용({rateLabel}%):
                      <span className="font-bold">
                        {finalPrice.toLocaleString()}원
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}
                </>
              );
            })()}
          </div>

          <div className="mt-10 mb-4 border-y py-4">
            <h2 className="text-xl font-bold text-gray-800">배송정보 입력</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-10">
            <div>
              <label className="block font-semibold mb-1">받는 사람</label>
              <input
                type="text"
                name="customerName"
                value={form.customerName}
                onChange={handleChange}
                className="w-full border rounded p-2"
                required
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">
                받는 사람 연락처 (숫자만 입력하세요)
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={(e) => {
                  // 숫자만 허용
                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  setForm((prev) => ({ ...prev, phoneNumber: onlyNums }));
                }}
                className="w-full border rounded p-2"
                pattern="^01[0-9]{8,9}$" // 010, 011, 016 등으로 시작, 총 10~11자리
                inputMode="numeric" // 모바일에서 숫자 키패드 표시
                maxLength={11} // 최대 11자리
                required
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">받는 주소</label>
              <div className="flex space-x-2 mb-2">
                <button
                  type="button"
                  onClick={() => {
                    openPost();
                  }}
                  className="bg-blue-500 text-white px-3 rounded"
                >
                  주소 검색
                </button>
              </div>

              {/* 기본 주소 */}
              <input
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                readOnly
                className="w-full border rounded p-2 mb-2"
                onClick={() => openPost()}
                required
              />

              {/* 상세 주소 */}
              <input
                type="text"
                name="detailAddress"
                value={form.detailAddress || ""}
                onChange={handleChange}
                placeholder="상세 주소 입력"
                className="w-full border rounded p-2"
                required
              />
            </div>
            <div className="mt-40"></div>
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-5 rounded hover:bg-blue-700 transition"
            >
              신청하기
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default CartProduct;
